import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { addressSearchService } from "src/app/services/api/location.service";
import { AddressResult } from "src/app/@types/interfaces/mar-api.interface";
import { buildSearchString } from "src/app/utils/address.utils";
import CloseIcon from "@mui/icons-material/Close";
import { quadrants } from "src/app/constants/address.constants";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
  "&:hover": { backgroundColor: theme.palette.action.selected },
  cursor: "pointer",
}));

const AddressSearchComponent = () => {
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [quadrant, setQuadrant] = useState("");
  const [searchResults, setSearchResults] = useState<AddressResult[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<AddressResult | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const isFormComplete = streetNumber.trim() && streetName.trim() && quadrant;

  const searchAddresses = async () => {
    if (!isFormComplete) return;

    try {
      setLoading(true);
      setError("");

      const searchAddress = buildSearchString(
        streetNumber,
        streetName,
        quadrant
      );

      const response = await addressSearchService.searchAddress(searchAddress);

      if (response.success && response.data?.addresses) {
        setSearchResults(response.data.addresses);
        setShowPopup(true);
      } else {
        setError(response.message || "Failed to search addresses");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred while searching"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFormComplete) {
      searchAddresses();
    } else {
      setSearchResults([]);
      setError("");
    }
  }, [streetNumber, streetName, quadrant]);

  const handleAddressSelect = (address: AddressResult) => {
    setSelectedAddress(address);
    setShowPopup(false);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <Card variant="outlined" className="p-12 m-2 mt-24">
      {/* Search Form */}
      <div className="flex grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-16 content-start ml-32 mr-32 mb-24">
        <div className="w-full">
          <label className="font-bold ml-8 mb-8 block">Street Number</label>
          <TextField
            placeholder="Enter street number"
            value={streetNumber}
            onChange={(e) => setStreetNumber(e.target.value)}
            size="small"
            className="w-full"
            variant="outlined"
          />
        </div>

        <div className="w-full">
          <label className="font-bold ml-8 mb-8 block">Street Name</label>
          <TextField
            placeholder="Enter street name"
            value={streetName}
            onChange={(e) => setStreetName(e.target.value)}
            size="small"
            className="w-full"
            variant="outlined"
          />
        </div>

        <div className="w-full">
          <label className="font-bold ml-8 mb-8 block">Quadrant</label>
          <FormControl size="small" className="w-full">
            <Select
              value={quadrant}
              onChange={(e) => setQuadrant(e.target.value)}
              displayEmpty
              variant="outlined"
            >
              <MenuItem value="" disabled>
                Select quadrant
              </MenuItem>
              {quadrants.map((quad) => (
                <MenuItem key={quad} value={quad}>
                  {quad}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Selected Address */}
      {selectedAddress && (
        <div className="ml-32 mr-32 mb-24">
          <Typography className="text-18 mb-16 font-semibold">
            Selected Address
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="selected address table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Full Address</StyledTableCell>
                  <StyledTableCell>Location Type</StyledTableCell>
                  <StyledTableCell>Ward</StyledTableCell>
                  <StyledTableCell>Zip Code</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow key={selectedAddress.marId}>
                  <StyledTableCell>
                    {selectedAddress.fullAddress}
                  </StyledTableCell>
                  <StyledTableCell>
                    {selectedAddress.locationType}
                  </StyledTableCell>
                  <StyledTableCell>{selectedAddress.ward}</StyledTableCell>
                  <StyledTableCell>{selectedAddress.zipcode}</StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setSelectedAddress(null)}
            >
              Clear Selection
            </Button>
          </Box>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="ml-32 mr-32">
          <Alert severity="error" className="mb-16">
            {error}
          </Alert>
        </div>
      )}

      {/* Search Results Popup */}
      <Dialog
        open={showPopup}
        onClose={handleClosePopup}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Search Results</Typography>
            <IconButton onClick={handleClosePopup}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress size={40} />
            </Box>
          )}

          {searchResults.length > 0 && !loading && (
            <TableContainer component={Paper}>
              <Table size="small" aria-label="address search results table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Full Address</StyledTableCell>
                    <StyledTableCell>Location Type</StyledTableCell>
                    <StyledTableCell>Ward</StyledTableCell>
                    <StyledTableCell>Zip Code</StyledTableCell>
                    <StyledTableCell>Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((result, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{result.fullAddress}</StyledTableCell>
                      <StyledTableCell>{result.locationType}</StyledTableCell>
                      <StyledTableCell>{result.ward}</StyledTableCell>
                      <StyledTableCell>{result.zipcode}</StyledTableCell>
                      <StyledTableCell>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleAddressSelect(result)}
                        >
                          Select
                        </Button>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {searchResults.length === 0 && !loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              className="mt-16"
            >
              No addresses found for the given search criteria.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AddressSearchComponent;
